import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateProjectSheet } from "components/common/CreateProjectSheet";
import { ProjectQuotaModal } from "components/common/modals/ProjectQuotaModal";
import { PurchaseProjectModal } from "components/common/modals/PurchaseProjectModal";
import { useCreateProjectPermissionGuard } from "components/common/CreateProjectPermissionGuard";
import type { ProjectDataType } from "store/types/project";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace/useWorkspaceIdentifiers";
import { useWorkspaceAuth } from "hooks/useWorkspace/useWorkspaceAuth";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { useProjectIdentifiers } from "hooks/project/useProjectIdentifiers";
import { Permissions } from "utils/permissions/permissionConstants";
import { PATH_DASHBOARD } from "config";
import { handleToast } from "utils/toasts";
import type { ProjectCreateSource } from "./types/project-create-source.types";

type CreateProjectFlowProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (project: ProjectDataType) => void;
  existingProject?: Partial<ProjectDataType>;
  mode?: "project" | "version";
  initialSource?: ProjectCreateSource;
  initialFile?: File | null;
  entrySource?: "onboarding" | "projects" | "sidebar";
};

export const CreateProjectFlow = ({
  isOpen,
  onClose,
  onSuccess,
  existingProject,
  mode = "project",
  initialSource,
  initialFile,
  entrySource,
}: CreateProjectFlowProps) => {
  const navigate = useNavigate();
  const { permissions, canCreateProject } = useCreateProjectPermissionGuard(existingProject);
  const { workspaceId } = useWorkspaceIdentifiers();
  const { projectId } = useProjectIdentifiers();
  const { canWorkspace, isLoading: isWorkspaceAuthLoading } = useWorkspaceAuth(workspaceId);
  const { canProject, isLoading: isProjectAuthLoading } = useProjectAuth(projectId);
  const [showSheet, setShowSheet] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowSheet(false);
      setShowPurchaseModal(false);
      setShowQuotaModal(false);
      return;
    }

    if (mode === "version") {
      if (isProjectAuthLoading) {
        return;
      }
      if (!canProject(Permissions.PROJECT_VERSION_CREATE)) {
        handleToast(
          "You do not have permission to create a new version for this project.",
          "error"
        );
        onClose();
        return;
      }
      setShowSheet(true);
      return;
    }

    if (isWorkspaceAuthLoading) {
      return;
    }
    if (!canWorkspace(Permissions.WORKSPACE_PROJECT_CREATE)) {
      handleToast("You do not have permission to create projects in this workspace.", "error");
      onClose();
      return;
    }

    if (permissions.isLoading) {
      return;
    }

    if (canCreateProject || permissions.shouldShowCreateSheet) {
      setShowSheet(true);
      return;
    }

    if (permissions.shouldShowPurchaseModal) {
      setShowPurchaseModal(true);
      return;
    }

    if (permissions.shouldShowQuotaModal) {
      setShowQuotaModal(true);
      return;
    }

    if (permissions.shouldRedirectToActivation) {
      onClose();
      navigate(PATH_DASHBOARD.activateAccount);
      return;
    }

    if (permissions.shouldRedirectToSelectPlan) {
      onClose();
      navigate(PATH_DASHBOARD.selectPlan);
    }
  }, [
    isOpen,
    mode,
    isWorkspaceAuthLoading,
    isProjectAuthLoading,
    canWorkspace,
    canProject,
    permissions.isLoading,
    permissions.shouldShowCreateSheet,
    permissions.shouldShowPurchaseModal,
    permissions.shouldShowQuotaModal,
    permissions.shouldRedirectToActivation,
    permissions.shouldRedirectToSelectPlan,
    canCreateProject,
    navigate,
    onClose,
  ]);

  const handleCloseFlow = () => {
    setShowSheet(false);
    setShowPurchaseModal(false);
    setShowQuotaModal(false);
    onClose();
  };

  return (
    <>
      <ProjectQuotaModal
        name="project-quota"
        open={isOpen && showQuotaModal}
        setOpen={handleCloseFlow}
      />
      <PurchaseProjectModal
        open={isOpen && showPurchaseModal}
        onClose={handleCloseFlow}
        mode={permissions.purchaseModalMode}
        projectId={existingProject?.project_id}
      />

      {isOpen && showSheet && (
        <CreateProjectSheet
          isOpen
          onClose={handleCloseFlow}
          onSuccess={onSuccess}
          existingProject={existingProject}
          mode={mode}
          initialSource={initialSource}
          initialFile={initialFile}
          entrySource={entrySource}
        />
      )}
    </>
  );
};
